import template from './log_time.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';
import * as Navbar from '../../lib/update_navbar_notifications.js';
import * as Permissions from '../../lib/permission_utils.js';

function format_date(dateStr)
{
	if (!dateStr) return null;
	let date = new Date(dateStr);
	return date.getFullYear() + '/' + 
		String(date.getMonth() + 1).padStart(2, '0') + '/' + 
		String(date.getDate()).padStart(2, '0') + ' ' +
		String(date.getHours()).padStart(2, '0') + ':' + 
		String(date.getMinutes()).padStart(2, '0');
}

class LogTimeViewModel
{
	constructor(dialog)
	{
		this.dialog = dialog;

		//edit mode
		this.task_time_id = ko.observable();
		this.approval_route = ko.observableArray();

		//the rest
		this.project_name = ko.observable();
		this.task_id = ko.observable();
		this.projects = ko.observableArray();
		this.description = ko.observable();
		this.start_time = ko.observable();
		this.end_time = ko.observable();
		this.rejection_reason = ko.observable();
		this.total_hours = ko.observable(0);
		this.from_filter = ko.observable(Utils.toJSONDateString(new Date()));
		this.to_filter = ko.observable(Utils.toJSONDateString(new Date()));
		this.tasks_logged = ko.observableArray();
		this.view_only = ko.observable(false);
		this.company_id = ko.observable();
		this.employee_id = ko.observable();
		this.transaction_types = ko.observableArray();
		this.transaction_type_id = ko.observable();
		this.resubmit = ko.observable(false);
		this.rejected_id = ko.observable();
		this.today = ko.observable(Utils.toJSONDateString(new Date()));
		this.holidays = ko.observableArray([]);
		this.event_dates = ko.observableArray([]);
		this.start_date_picker = null;
		this.end_date_picker = null;
		this.summary_filter_earned_end = ko.observable(Utils.toJSONDateString(new Date())); // today
		this.leave_summary = ko.observableArray();
		this.department_id = ko.observable();
		this.approval_status = ko.observable();

		//lunch
		this.include_lunch = ko.observable(false);
		this.lunch_start = ko.observable();
		this.lunch_end = ko.observable();
		this.is_approver = ko.observable(false);
		this.approval_structure_id = ko.observable();
		this.is_initializing = false; // Flag to prevent auto-updates during initialization

		this.from_filter.subscribe((d) => {
			if (d > this.to_filter())
				this.to_filter(d);

			this.dialog.updateData();
		})

		this.to_filter.subscribe((d) => {
			if (d < this.from_filter())
				this.from_filter(d);

			this.dialog.updateData();
		})

		this.start_time.subscribe((val) => {
			if (val && !this.is_initializing)
			{
				let start_date = new Date(val);
				
				// Only auto-set end time if it's not already set or if start time is after end time
				if (!this.end_time() || new Date(this.end_time()) <= start_date)
				{
					let end_date = new Date(start_date.getTime() + (60 * 60 * 8000)); // Add 8 hours

					if (this.end_date_picker)
					{
						this.end_date_picker.set('minDate', val); // Ensure end date can't be before start date
						this.end_date_picker.setDate(end_date);
					}
					this.end_time(this.formatting_date(end_date));
				}
				else if (this.end_date_picker)
				{
					// Just update the minDate constraint
					this.end_date_picker.set('minDate', val);
				}
			}

			this.validate_and_calculate_hours();
		});

		this.end_time.subscribe((val) => {
			if (val) 
				this.validate_and_calculate_hours();
		});

		this.total_hours.subscribe((d) => {
			//if both then total_hours must be set to the difference, it cant be bigger
			if (this.start_time() && this.end_time())
			{
				let hours = this.calculate_hours()
				if (d > hours)
				{
					this.total_hours(hours);
					Grape.alerts.alert(
						{
							type: 'error',
							message: tr('The total hours can not be more than the time difference'),
							title: tr('Error', 'Error')
						}
					);
				}
			}
		})

		this.task_id.subscribe((value) => {
			let selected_project = this.projects().find((project) => project.task_id === value);
			this.transaction_type_id(selected_project?.transaction_type_id || null);
		})
	}

	async initialize_datepickers(res_requests)
	{
		let calendar_config = {
			enableTime: true,
			dateFormat: "Y-m-d H:i",
			allowInput: true,
			time_24hr: true,
			onDayCreate: (dObj, dStr, fp, dayElem) => {
				let date_string = new Date(dayElem.dateObj.getTime() -
					(dayElem.dateObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
				
				let event = this.event_dates().find(e => e.date === date_string);
				
				if (event)
				{
					dayElem.classList.add(event.css_class);
					dayElem.style.backgroundColor = event.hexcolor;
					dayElem.setAttribute('title', event.name);
				}

				if (date_string == this.today)
				{
					dayElem.classList.add('today');
				}
			},
			appendTo: document.body,
			static: true,  
			onClose: (selectedDates, dateStr, instance) => {
				// Update the corresponding observable when the datepicker is closed
				if (instance.element.id === 'datepicker_start')
					this.start_time(dateStr);
				else if (instance.element.id === 'datepicker_end')
					this.end_time(dateStr);
			}
		};

		this.start_date_picker = flatpickr('#datepicker_start', {
			...calendar_config,
			defaultDate: this.start_time() || null
		});
		
		this.end_date_picker = flatpickr('#datepicker_end', {
			...calendar_config,
			defaultDate: this.end_time() || null
		});
	}

	async load_events()
	{
		try
		{
			let res_requests = await Grape.fetches.postJSON('/leave/get_leave_taken_and_requests',
				{ employee_id: this.employee_id() });
			
			if (res_requests.status !== 'ERROR')
			{
				this.parse_leave(res_requests);
				
				if (this.start_date_picker && this.end_date_picker)
				{
					this.start_date_picker.destroy();
					this.end_date_picker.destroy(); 
				}
				
				this.initialize_datepickers(res_requests);
			}
		} catch (error)
		{
			Grape.alerts.alert(
				{
					type: 'error',
					message: tr('Failed to load leave and holiday data'),
					title: tr('Error')
				}
			);
		}
	}

	async fetch_approval_status()
	{
		if (!this.approval_structure_id()) return;
		
		// Check if user has TimeManagementApproval feature
		const hasApprovalFeature = await Permissions.checkAccessRights('TimeManagementApproval');
		if (!hasApprovalFeature) return;
		
		// Employees should not be able to approve their own time
		if (Utils.current_employee_id() === this.employee_id()) return;
		
		let approval_table = {
			table: 'time_management_approval_structure_line',
			schema: 'public',
			filter: [
				{
					value: this.approval_structure_id(),
					operand: '=',
					field: 'approval_structure_id'
				},
				{
					value: Utils.current_employee_id(),
					operand: '=',
					field: 'employee_id'
				}
			]
		}
		let approval_data = await Grape.fetches.getJSON('api/record', approval_table);
		console.log('approval data', approval_data)

		if (approval_data && approval_data.records && approval_data.records.length > 0 && (this.approval_status() === 'pending' || this.approval_status() === 'waiting'))	
			this.is_approver(true);
		
	}

	async approveEntry()
	{

		let response = await Grape.alerts.confirm({type: 'warning', title: tr('Warning'), message: tr("Are you sure you want to approve the entry?")});
		if (response)
		{
			let res = await Grape.fetches.postJSON('/time_management/approve_time_logged_entry',
			{
				task_time_id: this.task_time_id(),
				employee_id: this.employee_id() 
			});
			if (res.status == 'OK')
			{
				Grape.alerts.alert({ type: 'success', message: tr('Successfully approved time logged entry.'), title: tr('Success') });
				this.dialog.updateData();
				this.dialog.close();
			}
			else if (res.status == 'ERROR')
			{
				Grape.alerts.apiError(res);
				this.dialog.updateData();
			}
			else if (res.error)
			{
				Grape.alerts.alert({ type: 'error', message: res.error, title: tr('Error') });
				this.dialog.updateData();
			}
			else
				Grape.alerts.alert({ type: 'error', message: tr('Something went wrong...'), title: tr('Error') });
		};
	}
	
	async rejectEntry()
	{
		await Grape.dialog.open('TimeLoggedReject', { 
			task_time_id: this.task_time_id()
		});
		this.dialog.close();
	}


	parse_leave(res)
{
	let event_dates = [];

	// Parse finalized leave
	if (res.all_finalized_leave && res.all_finalized_leave.length > 0) 
		event_dates = event_dates.concat(this.parse_final_leave(res.all_finalized_leave));

	// Parse leave to approve - BUT skip rejected leave requests
	if (res.all_leave_to_approve && res.all_leave_to_approve.length > 0)
	{
		// Filter out rejected leave requests before processing
		let approvedLeaveRequests = res.all_leave_to_approve.filter(leave => leave.approved !== 'rejected');
		
		if (approvedLeaveRequests.length > 0) {
			let requestedDates = this.parse_final_dates(approvedLeaveRequests.flatMap(leave => leave.dates_included));
			event_dates = event_dates.concat(requestedDates);
		}
	}

	// Parse holidays (assuming holidays are included in the response)
	if (this.holidays && this.holidays().length > 0)
	{
		let holiday_dates = this.parse_holidays(this.holidays());
		event_dates = event_dates.concat(holiday_dates);
	}

	this.event_dates(event_dates);
}
	
	parse_final_leave(finalizedLeave)
	{
		return finalizedLeave.flatMap(leave =>
			leave.dates_included.map(date => ({
				date: date,
				css_class: leave.css_class_name,
				hexcolor: leave.hexcolor,
				name: `${leave.leave_type} (Finalised)`
			}))
		);
	}
	
	parse_final_dates(dateArray)
	{
		return dateArray.map(date => ({
			date: date,
			css_class: 'request_date',
			hexcolor: '#fff3cd',
			name: 'Requested Leave day'
		}));
	}
	
	parse_holidays(holidays)
	{
		return holidays.map(holiday => ({
			date: holiday.holiday_date,
			css_class: holiday.css_class_name,
			hexcolor: holiday.hexcolor,
			name: holiday.description
		}));
	}

	is_same_day(date1, date2)
	{
		return date1.getFullYear() === date2.getFullYear() &&
			date1.getMonth() === date2.getMonth() &&
			date1.getDate() === date2.getDate();
	}

	formatting_date(date) {
		return date.getFullYear() + '/' +
			String(date.getMonth() + 1).padStart(2, '0') + '/' +
			String(date.getDate()).padStart(2, '0') + ' ' +
			String(date.getHours()).padStart(2, '0') + ':' +
			String(date.getMinutes()).padStart(2, '0');
	}



	validate_and_calculate_hours()
	{
		if (this.start_time() && this.end_time())
		{
			let start = new Date(this.start_time());
			let end = new Date(this.end_time());

			// Check if dates are valid
			if (isNaN(start.getTime()) || isNaN(end.getTime()))
			{
				return;
			}

			if (end < start)
			{
				Grape.alerts.alert(
					{
						type: 'warning',
						message: tr('End time cannot be set earlier than start time.'),
						title: tr('Warning', 'Warning')
					}
				);
				
				this.end_time(''); // Reset end time
				this.total_hours(0);
			}
			else
				this.total_hours(this.calculate_hours());
		}
	}

	calculate_hours()
	{
		if (!this.start_time() || !this.end_time())
			return 0;

		let start_time = new Date(this.start_time());
		let end_time = new Date(this.end_time());
		let diff = (end_time.getTime() - start_time.getTime()) / 1000;
		diff /= 60 * 60;
		return Math.abs(Number(diff).toFixed(2));
	}

	close ()
	{
		this.dialog.close();
	}

	async save_entry(force_save = false)
	{
		// Initial validations
		if (!this.task_id() && !this.project_name())
		{
			Grape.alerts.alert({type: 'error', message: tr('Please provide either a new task name or select an already existing task name'), title: tr('Error')});
			return;
		}
		if (!this.description())
		{
			Grape.alerts.alert({type: 'error', message: tr('Please add a description'), title: tr('Error')});
			return;
		}
		if (!this.start_time())
		{
			Grape.alerts.alert({type: 'error', message: tr('Please select a valid start time'), title: tr('Error')});
			return;
		}
		if (!this.end_time())
		{
			Grape.alerts.alert({type: 'error', message: tr('Please select a valid end time'), title: tr('Error')});
			return;
		}
		// Validate end time is after start time using Date objects
		let start_date_check = new Date(this.start_time());
		let end_date_check = new Date(this.end_time());
		if (end_date_check <= start_date_check)
		{
			Grape.alerts.alert({type: 'error', message: tr('End time must be after start time'), title: tr('Error')});
			return;
		}
		if (!this.total_hours() || this.total_hours() < 0)
		{
			Grape.alerts.alert({type: 'error', message: tr('Please select the total number of hours as a positive number'), title: tr('Error')});
			return;
		}

		if (Grape.config.public_settings.time_rule_priority)
		{
			console.log('time rules priority is active');
			//fetch time rules for employee_id

			let time_rule_data = {
				schema: 'public',
				table: 'time_rules',
				filter: [{
					field: 'employee_id',
					operand: '=',
					value: Utils.current_employee_id()
				}]
			}

			let time_rule_res = await Grape.fetches.getJSON('api/record', time_rule_data);
			console.log('time_rules', time_rule_res)

			if (time_rule_res.records.length >= 1)
			{
				console.log('have time rules setup')
			}
			else
			{
				Grape.alerts.alert({type: 'warning', message: tr('Current employee does not have time rules please add time rules to employee or speak to manager to add time rules to log time.'), title: tr('Warning')});
				return;
			}
		}
	
		// Lunch validation
		if (this.include_lunch())
		{
			if (!this.lunch_start() && !this.lunch_end())
			{
				Grape.alerts.alert({type: 'warning', message: tr('Please enter start and end time for lunch'), title: tr('Warning')});
				return;
			}
			if (!this.lunch_start() )
			{
				Grape.alerts.alert({type: 'warning', message: tr('Please enter a start time for lunch'), title: tr('Warning')});
				return;
			}
			if (!this.lunch_end() )
			{
				Grape.alerts.alert({type: 'warning', message: tr('Please enter a end time for lunch'), title: tr('Warning')});
				return;
			}
			if (this.lunch_start() >= this.lunch_end())
			{
				Grape.alerts.alert({type: 'warning', message: tr('Start time of lunch has to be before  the end time'), title: tr('Warning')});
				return;
			}
		}
	
		// Check for leave/holiday conflicts
		let start_date = new Date(this.start_time());
		let end_date = new Date(this.end_time());
		let formatted_start_date = start_date.toISOString().split('T')[0];
		let formatted_end_date = end_date.toISOString().split('T')[0];
	
		// Check holidays and leave dates
		let holiday = this.holidays().find(h => 
			h.holiday_date === formatted_start_date || h.holiday_date === formatted_end_date
		);
		
		let leave_dates = this.event_dates().filter(e => 
			(e.date === formatted_start_date || e.date === formatted_end_date) && 
			(e.css_class === 'request_date' || e.name.includes('Finalised'))
		);
	
		// Determine the type of date conflict
		let date_status = holiday ? 'HOLIDAY' : leave_dates.length > 0 ? 'LEAVE' : 'no_conflict';
	
		let should_proceed = true;
		
		switch (date_status) {
			case 'HOLIDAY': {
				const response = await Grape.alerts.confirm({ 
					message: tr('This is a holiday. Are you sure you want to log this time?'),
					title: tr('Warning'), 
					type: 'warning' 
				});
				should_proceed = response;
				break;
			}
			case 'LEAVE': {
				const response = await Grape.alerts.confirm({ 
					message: tr('This is a leave date. Are you sure you want to log this time?'),
					title: tr('Warning'), 
					type: 'warning' 
				});
				should_proceed = response;
				break;
			}
			case 'no_conflict':
				should_proceed = true;
				break;
		}
	
		if (!should_proceed) return;
	
		// Prepare parameters for saving
		let params = {
			employee_id: this.employee_id() || Utils.current_employee_id(),
			description: this.description(),
			start_time: this.start_time(),
			end_time: this.end_time(),
			total_hours: this.total_hours(),
			project_text: this.project_name(),
			project_id: this.task_id() || null,
			task_time_id: this.task_time_id() || -1,
			rejected_id: this.rejected_id(),
			transaction_type_id: this.transaction_type_id() || null,
			company_id: this.company_id(),
			force: force_save // Add force flag to bypass overlap check
		};
	
		// Add lunch parameters if included
		if (this.include_lunch())
		{
			params.lunch_start = moment(this.start_time().substring(0, 10) + ' ' + this.lunch_start()).format();
			params.lunch_end = moment(this.end_time().substring(0, 10) + ' ' + this.lunch_end()).format();
		}
	
		
		let res = await Grape.fetches.postJSON('/time_management/edit_task_time', params);
		
		// Handle overlap warning
		if (res.status === 'WARNING' && res.code === 'OVERLAP_WARNING')
		{
			const confirm_overlap = await Grape.alerts.confirm({
				message: tr('The time entered overlaps with an existing time entry. Do you want to save the time anyway?'),
				title: tr('Overlap Detected'),
				type: 'warning'
			});
			
			if (confirm_overlap)
			{
				// Retry with force flag
				await this.save_entry(true);
			}
			return;
		}
		
		switch (res.status)
		{
			case 'OK': {
				let success_message = params.task_time_id === -1 ? tr('Successfully added time log.') : tr('Successfully edited time log.');
					
				await Grape.alerts.alert({
					type: 'success',
					message: success_message,
					title: tr('Success')
				});
				await Navbar.updateTimeToApproveCount();
				this.dialog.close();
				break;
			}
			case 'ERROR': {
				Grape.alerts.apiError(res);
				break;
			}
			default: {
				Grape.alerts.alert({
					type: 'error', 
					message: tr('Something went wrong...'), 
					title: tr('Error')
				});
			}
		}
	}

	async is_not_view_only_and_not_resubmit ()
	{
		return !this.view_only() && !this.resubmit();
	}
};

class LogTimePage
{
	constructor (bindings)
	{
		this.viewModel = new LogTimeViewModel(this);
		this.bindings = bindings;
		document.querySelector('.ps-dialog').classList.add('log-time-dialog');
	};

	async init ()
	{
		console.log('bindings', this.bindings);

		if (this.bindings.employee_id)
			this.viewModel.employee_id(this.bindings.employee_id)
		else
			this.viewModel.employee_id(Utils.current_employee_id());

		let res = await Grape.fetches.postJSON('/time_management/get_projects', {});
		
		if (res.projects)
			this.viewModel.projects(res.projects);

		let employee_id = this.viewModel.employee_id();
		console.log('employee_id', employee_id);
		let company_id = await Utils.current_user_company_id(employee_id);
		this.viewModel.company_id(company_id);

		console.log('company', company_id) 

		//get projects
		let res_projects = await Grape.fetches.getJSON("/api/record", {
			table: "v_projects",
			schema: "savanna",
			filter: [{ value: company_id, operand: "=", field: "company_id" }],
		});

		console.log('project result ', res_projects);
		//set bindings
		if (res_projects?.records)
		{
			for (let i = 0; i < res_projects.records.length; i++)
			{
				let record = res_projects.records[i];
				let userDescription = record.user_description || "None";
				record.task_description = `${record.task_description} (${userDescription})`;
			}

			this.viewModel.projects(res_projects.records);
		}

		await this.get_transaction_types();

		//edit mode
		if (this.bindings.row)
		{
			console.log('bindings', this.bindings.row);
			this.viewModel.employee_id(this.bindings.row.employee_id);
			this.viewModel.task_time_id(this.bindings.row.task_time_id);
			this.viewModel.description(this.bindings.row.description || '');
			this.viewModel.task_id(this.bindings.row.project_task_id || null);
			this.viewModel.department_id(this.bindings.row.department_id || null);
			this.viewModel.company_id(this.bindings.row.company_id || null);
			this.viewModel.approval_structure_id(this.bindings.row.approval_structure_id || null);
			this.viewModel.approval_status(this.bindings.row.approval_status || null);
			
			console.log('Approval structure ID:', this.bindings.row.approval_structure_id);
			console.log('Approval status:', this.bindings.row.approval_status);
			
			this.viewModel.rejection_reason(this.bindings.row.rejection_reason || null); 
			this.viewModel.total_hours(this.bindings.row.total_hours || 0);
			this.viewModel.transaction_type_id(this.bindings.row.id || null);
			this.viewModel.lunch_start(this.bindings.row.lunch_start || null);
			this.viewModel.lunch_end(this.bindings.row.lunch_end || null);

			 // Fix lunch data initialization
			if (this.bindings.row.lunch_start && this.bindings.row.lunch_end)
			{
				this.viewModel.include_lunch(true);
				this.viewModel.lunch_start(this.bindings.row.lunch_start)
				this.viewModel.lunch_end(this.bindings.row.lunch_end)
			}
			else 
				this.viewModel.include_lunch(false);
			

			await this.viewModel.initialize_datepickers();
			await this.viewModel.fetch_approval_status();

			// FIXED: Parse and format dates properly before setting to datepickers
			// Set flag to prevent auto-updates during initialization
			this.viewModel.is_initializing = true;
			
			let start_date = new Date(this.bindings.row.start_time);
			let end_date = new Date(this.bindings.row.end_time);
			
			// Format dates using the formatting_date method
			let formatted_start = this.viewModel.formatting_date(start_date);
			let formatted_end = this.viewModel.formatting_date(end_date);
			
			// Set the datepickers with Date objects
			this.viewModel.start_date_picker.setDate(start_date);
			this.viewModel.end_date_picker.setDate(end_date);
			
			// Set the observables with properly formatted strings
			this.viewModel.start_time(formatted_start);
			this.viewModel.end_time(formatted_end);
			
			// Clear the initialization flag after a short delay
			setTimeout(() => {
				this.viewModel.is_initializing = false;
			}, 100);
						
			//view only
			if (this.bindings.row.view_only)
				this.viewModel.view_only(true);

			//resubmit
			if (this.bindings.row.resubmit)
			{
				this.viewModel.resubmit(true);
				this.viewModel.task_time_id(null);
				this.viewModel.rejected_id(this.bindings.row.task_time_id);
		
			}
		}

		this.updateData();

		//get approval structure
		let res_route = await Grape.fetches.postJSON('/time_management/get_approval_route',
			{
				employee_id: this.viewModel.employee_id(),
				task_time_id: this.viewModel.task_time_id()
			}
		);
		
		if (res_route.status != 'ERROR')
		{
			if (res_route.approvers)
				this.viewModel.approval_route(res_route.approvers);
			else
				this.viewModel.approval_route(null);
		}

		let res_leave = await Grape.fetches.getJSON('/leave/' +
			this.viewModel.employee_id() +
			'/summary/2000-01-01/' +
			this.viewModel.summary_filter_earned_end(), {}
		);
		
		if (res_leave.status !== 'ERROR')
			this.viewModel.leave_summary(ko.mapping.fromJS(res_leave));
		
		await this.load_calendar_colors();
		await this.load_holidays();
		await this.viewModel.load_events();
		
		this.viewModel.initialize_datepickers();
		this.add_custom_calendar_css();
	};



	async updateData ()
	{
		let res = await Grape.fetches.postJSON('/time_management/get_time_logged_employee',
			{
				employee_id: this.viewModel.employee_id(),
				start_date: this.viewModel.from_filter(),
				end_date: this.viewModel.to_filter()
			}
		);

		if (res.status != 'ERROR')
		{
			if (res.time_logged)
			{
				for (let i = 0; i < res.time_logged.length; i++)
				{
					res.time_logged[i].start_time = Utils.get_timestamp(new Date(res.time_logged[i].start_time))

					if (res.time_logged[i].end_time)
						res.time_logged[i].end_time = Utils.get_timestamp(new Date(res.time_logged[i].end_time))
				}
				this.viewModel.tasks_logged(res.time_logged); 
			}
			else
				this.viewModel.tasks_logged([]); 
		}
	};
	async get_transaction_types ()
	{
		//get all the employees
		let filter = [];
		filter = [
			{
				value: this.viewModel.company_id(),
				operand: "=",
				field: "company_id",
			}
		];

		let res = await Grape.fetches.getJSON("/api/record",
			{
				table: "transaction_type_user",
				schema: "public",
				limit: 1000000,
				filter: filter,
			}
		);

		if (res.records != null)
			this.viewModel.transaction_types(res.records);
	};
	async load_calendar_colors()
	{
		let res = await Grape.fetches.postJSON('/general/get_leave_color_combinations', {});
		generateCSScolorCombinations(res);
	}

	async load_holidays()
	{
		let holiday_res = await Grape.fetches.postJSON('/leave/get_holidays', {});	

		if (holiday_res?.get_holidays)
			this.viewModel.holidays(holiday_res.get_holidays);
		else
			this.viewModel.holidays.removeAll();
	}

	add_custom_calendar_css()
	{
		const style = document.createElement('style');
		style.textContent = `
			.log-time-dialog .flatpickr-calendar.end-date-calendar { 
				left: -1em;
			}

			.log-time-dialog .flatpickr-calendar.static.open {
				display: inline-grid;
			}

			.log-time-dialog .dayContainer {
				height: 15.5em;
				min-width: 238px;
				max-width: 238px;
			}

			.log-time-dialog .flatpickr-days {
				width: 238px !important;
			}

			.log-time-dialog .flatpickr-day {
				font-weight: 400;
				width: 34px;
				-webkit-flex-basis: 34px;
				-ms-flex-preferred-size: 34px;
				flex-basis: 34px;
				max-width: 34px;
				height: 33px;
				line-height: 31px;
				display: inline-block;
				margin: 0;
				text-align: center;
				padding: 0;
			}

			.log-time-dialog .flatpickr-calendar.open, 
			.log-time-dialog .flatpickr-calendar.inline {
				opacity: 1;
				max-height: 640px;
				visibility: visible;
				height: 281px;
				width: 238px;
			}

			.log-time-dialog .flatpickr-month {
				height: 34px;
				display: flex;
				align-items: center;
				padding: 4px 0;
			}

			.log-time-dialog .flatpickr-current-month {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 101%;
				height: 35px;
				padding: 1;
				font-size: 15px;
			}

			.log-time-dialog .flatpickr-current-month .cur-month {
				margin-right: 4px;
			}

			.log-time-dialog .numInputWrapper {
				width: 54px;
				margin-right: 0.5em;
				margin-left: 0.5em;
				font-size: 11px;
			}

			.log-time-dialog .flatpickr-monthDropdown-months {
				font-size: 17px;
				font-weight: 400;
				height: 22px;
				padding: 0 4px;
			}

			.log-time-dialog .numInput.cur-year {
				font-size: 14px;
				height: 22px;
				padding: 0 4px;
			}

			.log-time-dialog .flatpickr-weekdays {
				height: 28px;
				display: flex;
				align-items: center;
			}

			.log-time-dialog .flatpickr-weekday {
				width: 34px;
				max-width: 34px;
				flex: 0 0 34px;
				margin: 0;
				text-align: center;
				font-size: 12px;
			}

			.log-time-dialog #datepicker_start + .flatpickr-calendar {
				left: -1em;
			}

			.log-time-dialog .flatpickr-months .flatpickr-prev-month,
			.log-time-dialog .flatpickr-months .flatpickr-next-month {
				height: 34px;
				padding: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
			}`;
		document.head.appendChild(style);

		// Apply the custom class to the end date picker
		if (this.viewModel.end_date_picker) 
			this.viewModel.end_date_picker.calendarContainer.classList.add('end-date-calendar');
	}
};

export default {
	name: 'LogTime',
	dialog_class: LogTimePage,
	template: template,
	template_filename: 'translation/dialogs/log_time',
	provider: 'ps'
};
